import * as React from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { CENTRAL_API_V3 } from '@studio/constants/env-variables';
import { calculateTokenExpiration } from '@preview/helpers/calculateTokenExpiration';
import {
  authStateAtom,
  isTokenExpiredAtom,
  setAuthRefreshingAtom,
  setCompleteAuthStateAtom,
} from '@preview/store/auth';

export function useRefreshToken() {
  const authState = useAtomValue(authStateAtom);
  const isTokenExpired = useAtomValue(isTokenExpiredAtom);
  const setAuthRefreshing = useSetAtom(setAuthRefreshingAtom);
  const setCompleteAuthState = useSetAtom(setCompleteAuthStateAtom);

  const refreshToken = React.useCallback(async (): Promise<{ error?: string; success: boolean; token?: string }> => {
    try {
      setAuthRefreshing(true);
      const tokenUrl = `${CENTRAL_API_V3}/auth/token`;

      const response = await fetch(tokenUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          grant_type: 'refresh_token_cookie',
        }),
      });

      if (!response.ok) {
        throw new Error(`Token refresh failed: ${response.status}`);
      }

      const data = await response.json();
      const newToken = data.access_token;
      const expiresIn = data.expires_in;
      const tokenExpiresAt = calculateTokenExpiration(expiresIn);

      setCompleteAuthState({
        token: newToken,
        tokenExpiresAt,
        isExpired: false,
        isRefreshing: false,
        error: null,
        refreshTokenError: null,
      });

      return { success: true, token: newToken };
    } catch (error) {
      setCompleteAuthState({
        token: null,
        tokenExpiresAt: null,
        isExpired: true,
        isRefreshing: false,
        error: null,
        refreshTokenError: error instanceof Error ? error : new Error('Token refresh failed'),
      });
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Token refresh failed',
      };
    }
  }, [setAuthRefreshing, setCompleteAuthState]);

  return {
    refreshToken,
    isRefreshing: authState.isRefreshing,
    isExpired: authState.isExpired || isTokenExpired,
    error: authState.error,
    refreshTokenError: authState.refreshTokenError,
  };
}
